import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
} from "react";
import { gsap } from "gsap";
import { motionSafe } from "../hooks";
import type { Item } from "../types";
import { cn } from "../lib";

const clamp = (value: number, min: number, max: number) =>
  Math.min(Math.max(value, min), max);

const layerColor = (
  faceColor: string,
  depthColor: string,
  index: number,
  total: number,
) => {
  const progress = total <= 1 ? 1 : index / total;
  const faceMix = Math.round((1 - progress * progress) * 72 + 4);
  return `color-mix(in srgb, ${faceColor} ${faceMix}%, ${depthColor})`;
};

export function DepthText({
  text,
  layers = 34,
  depth = 2.4,
  faceColor = "#f8fafc",
  depthColor = "#7c3aed",
  tilt = 7.5,
  pointerTracking = true,
  smoothing = 0.14,
  perspective = 900,
  autoOrbit = true,
  orbitSpeed = 0.35,
  fontSize = 104,
  fontWeight = 900,
  shadow = true,
}: {
  text: string;
  layers?: number;
  depth?: number;
  faceColor?: string;
  depthColor?: string;
  tilt?: number;
  pointerTracking?: boolean;
  smoothing?: number;
  perspective?: number;
  autoOrbit?: boolean;
  orbitSpeed?: number;
  fontSize?: number;
  fontWeight?: number;
  shadow?: boolean;
}) {
  const rootRef = useRef<HTMLHeadingElement>(null);
  const stageRef = useRef<HTMLSpanElement>(null);
  const safeLayers = clamp(Math.round(layers || 1), 2, 64);
  const safeDepth = clamp(depth || 0, 0, 12);
  const safeTilt = clamp(tilt || 0, -20, 20);
  const safeSmoothing = clamp(smoothing || 0.14, 0.02, 0.35);
  const safePerspective = clamp(perspective || 900, 300, 2000);
  const safeOrbitSpeed = clamp(orbitSpeed || 0, 0, 2);
  const baseRotation = useMemo(
    () => ({ x: -safeTilt * 0.32, y: safeTilt * 0.42 }),
    [safeTilt],
  );
  const depthLayers = useMemo(
    () =>
      Array.from({ length: safeLayers }, (_, layerIndex) => {
        const index = safeLayers - layerIndex;
        return {
          index,
          color: layerColor(faceColor, depthColor, index, safeLayers),
          transform: `translateZ(${-index * safeDepth}px)`,
        };
      }),
    [safeLayers, safeDepth, faceColor, depthColor],
  );

  useEffect(() => {
    const root = rootRef.current;
    const stage = stageRef.current;
    if (!root || !stage) return;
    const reduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    const finePointer = window.matchMedia(
      "(hover: hover) and (pointer: fine)",
    ).matches;
    const canTrack = pointerTracking && finePointer && !reduced;
    const current = { ...baseRotation };
    const target = { ...baseRotation };
    let frame = 0;
    let activePointer = false;
    const startTime = performance.now();
    const apply = () => {
      stage.style.transform = `rotateX(${current.x.toFixed(3)}deg) rotateY(${current.y.toFixed(3)}deg)`;
    };
    if (reduced) {
      apply();
      return;
    }
    const pointerMove = (event: PointerEvent) => {
      const rect = root.getBoundingClientRect();
      if (!rect.width || !rect.height) return;
      activePointer = true;
      const x = clamp(
        (event.clientX - (rect.left + rect.width / 2)) / (rect.width * 0.8),
        -1,
        1,
      );
      const y = clamp(
        (event.clientY - (rect.top + rect.height / 2)) / (rect.height * 0.8),
        -1,
        1,
      );
      target.x = baseRotation.x - y * safeTilt;
      target.y = baseRotation.y + x * safeTilt;
    };
    const pointerLeave = () => {
      activePointer = false;
      target.x = baseRotation.x;
      target.y = baseRotation.y;
    };
    if (canTrack) {
      root.addEventListener("pointermove", pointerMove);
      root.addEventListener("pointerleave", pointerLeave);
      window.addEventListener("blur", pointerLeave);
    }
    let visible = true;
    let disposed = false;
    const tick = (now: number) => {
      if ((!canTrack || !activePointer) && autoOrbit) {
        const orbit = ((now - startTime) / 1000) * safeOrbitSpeed * Math.PI * 2;
        const amount = canTrack ? 0.16 : 0.48;
        target.x = baseRotation.x + Math.sin(orbit) * safeTilt * amount;
        target.y = baseRotation.y + Math.cos(orbit * 0.85) * safeTilt * amount;
      }
      current.x += (target.x - current.x) * safeSmoothing;
      current.y += (target.y - current.y) * safeSmoothing;
      apply();
      frame = visible && !document.hidden ? requestAnimationFrame(tick) : 0;
    };
    apply();
    const start = () => {
      if (!disposed && visible && !document.hidden && !frame)
        frame = requestAnimationFrame(tick);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) start();
      else if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    }, { rootMargin: "120px" });
    const visibility = () => {
      if (document.hidden && frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      } else start();
    };
    observer.observe(root);
    document.addEventListener("visibilitychange", visibility);
    start();
    return () => {
      disposed = true;
      root.removeEventListener("pointermove", pointerMove);
      root.removeEventListener("pointerleave", pointerLeave);
      window.removeEventListener("blur", pointerLeave);
      document.removeEventListener("visibilitychange", visibility);
      observer.disconnect();
      cancelAnimationFrame(frame);
    };
  }, [
    autoOrbit,
    baseRotation,
    pointerTracking,
    safeOrbitSpeed,
    safeSmoothing,
    safeTilt,
  ]);

  const style = {
    "--rb-depth-perspective": `${safePerspective}px`,
    "--rb-depth-size": `${fontSize}px`,
    "--rb-depth-weight": fontWeight,
    "--rb-depth-face": faceColor,
    "--rb-depth-shadow": shadow
      ? `0 22px 34px color-mix(in srgb, ${depthColor} 36%, transparent), 0 4px 8px rgb(0 0 0 / .28)`
      : "none",
  } as CSSProperties;
  return (
    <h1 ref={rootRef} className="rb-depth-text" style={style} aria-label={text}>
      <span ref={stageRef} className="rb-depth-text__stage" aria-hidden="true">
        {depthLayers.map((layer) => (
          <span
            className="rb-depth-text__layer"
            key={layer.index}
            style={{ color: layer.color, transform: layer.transform }}
          >
            {text}
          </span>
        ))}
        <span className="rb-depth-text__face">{text}</span>
      </span>
    </h1>
  );
}

const MAX_PIXELS = 72;
type PixelPattern =
  | "random"
  | "center"
  | "edges"
  | "left-to-right"
  | "right-to-left"
  | "top-to-bottom"
  | "bottom-to-top"
  | "diagonal"
  | "spiral";
const patterns: Record<PixelPattern, (x: number, y: number) => number | null> =
  {
    random: () => null,
    center: (x, y) => Math.hypot(x - 0.5, y - 0.5) / Math.SQRT1_2,
    edges: (x, y) => Math.min(x, 1 - x, y, 1 - y) * 2,
    "left-to-right": (x) => x,
    "right-to-left": (x) => 1 - x,
    "top-to-bottom": (_x, y) => y,
    "bottom-to-top": (_x, y) => 1 - y,
    diagonal: (x, y) => (x + y) / 2,
    spiral: (x, y) => {
      const angle = (Math.atan2(y - 0.5, x - 0.5) + Math.PI) / (Math.PI * 2);
      const radius = Math.hypot(x - 0.5, y - 0.5) / Math.SQRT1_2;
      return (angle + radius) % 1;
    },
  };
const noise = (seed: number) => {
  const value = Math.sin(seed * 127.1 + 311.7) * 43758.5453;
  return value - Math.floor(value);
};
const coverScale = (size: number, gap: number, radius: number) => {
  const p = clamp(radius, 0, 50) / 100;
  const corner = Math.SQRT1_2 / (Math.SQRT2 * (0.5 - p) + p);
  return ((size + gap) / size) * Math.max(1, corner);
};
type Grid = ReturnType<typeof buildGrid>;
function buildGrid({
  width,
  height,
  pixelSize,
  gap,
  pattern,
  randomness,
}: {
  width: number;
  height: number;
  pixelSize: number;
  gap: number;
  pattern: PixelPattern;
  randomness: number;
}) {
  let size = pixelSize;
  let columns = Math.max(1, Math.ceil((width + gap) / (size + gap)));
  let rows = Math.max(1, Math.ceil((height + gap) / (size + gap)));
  if (columns * rows > MAX_PIXELS) {
    size = Math.ceil(size * Math.sqrt((columns * rows) / MAX_PIXELS));
    columns = Math.max(1, Math.ceil((width + gap) / (size + gap)));
    rows = Math.max(1, Math.ceil((height + gap) / (size + gap)));
  }
  const stride = size + gap;
  const originX = (width - (columns * stride - gap)) / 2;
  const originY = (height - (rows * stride - gap)) / 2;
  const order = patterns[pattern] ?? patterns.random;
  const mix = clamp(randomness, 0, 1);
  const pixels = [];
  for (let row = 0; row < rows; row += 1) {
    for (let column = 0; column < columns; column += 1) {
      const index = row * columns + column;
      const x = columns <= 1 ? 0.5 : column / (columns - 1);
      const y = rows <= 1 ? 0.5 : row / (rows - 1);
      const base = order(x, y);
      const random = noise(index + 1);
      pixels.push({
        id: index,
        left: originX + column * stride,
        top: originY + row * stride,
        offset: base === null ? random : base * (1 - mix) + random * mix,
      });
    }
  }
  return { pixels, size, gap, width, height };
}
export function PixelSwap({
  firstContent,
  secondContent,
  pixelSize = 64,
  gap = 0,
  pixelRadius = 0,
  pixelSpin = 0,
  pixelScale = 0.35,
  fade = true,
  duration = 1400,
  pixelDuration = 450,
  pattern = "random",
  randomness = 0,
  easing = "cubic-bezier(0.22, 1, 0.36, 1)",
  trigger = "hover",
  initialActive = false,
  active,
  onActiveChange,
  onComplete,
  aspectRatio = "16 / 10",
  className = "",
  style,
  hintContent,
  editing = false,
}: {
  firstContent: ReactNode;
  secondContent: ReactNode;
  pixelSize?: number;
  gap?: number;
  pixelRadius?: number;
  pixelSpin?: number;
  pixelScale?: number;
  fade?: boolean;
  duration?: number;
  pixelDuration?: number;
  pattern?: PixelPattern;
  randomness?: number;
  easing?: string;
  trigger?: "hover" | "click" | "manual";
  initialActive?: boolean;
  active?: boolean;
  onActiveChange?: (active: boolean) => void;
  onComplete?: (active: boolean) => void;
  aspectRatio?: string;
  className?: string;
  style?: CSSProperties;
  hintContent?: ReactNode;
  editing?: boolean;
}) {
  const [internalActive, setInternalActive] = useState(initialActive);
  const [shownActive, setShownActive] = useState(active ?? initialActive);
  const [transition, setTransition] = useState<{
    to: boolean;
    grid: Grid;
  } | null>(null);
  const [box, setBox] = useState({ width: 0, height: 0 });
  const containerRef = useRef<HTMLDivElement>(null);
  const pixelRefs = useRef<(HTMLDivElement | null)[]>([]);
  const animationsRef = useRef<Animation[]>([]);
  const timerRef = useRef<number>(0);
  const swapTimerRef = useRef<number>(0);
  const desiredActive = active ?? internalActive;
  const grid = useMemo(
    () =>
      buildGrid({
        width: box.width,
        height: box.height,
        pixelSize: Math.max(8, Math.round(pixelSize)),
        gap: Math.max(0, Math.round(gap)),
        pattern,
        randomness,
      }),
    [box.width, box.height, pixelSize, gap, pattern, randomness],
  );
  const configRef = useRef({
    duration,
    pixelDuration,
    pixelSpin,
    pixelScale,
    pixelRadius,
    fade,
    easing,
    onComplete,
  });
  const gridRef = useRef(grid);
  configRef.current = {
    duration,
    pixelDuration,
    pixelSpin,
    pixelScale,
    pixelRadius,
    fade,
    easing,
    onComplete,
  };
  gridRef.current = grid;
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const measure = () => {
      const width = container.clientWidth;
      const height = container.clientHeight;
      if (!width || !height) return;
      setBox((current) =>
        current.width === width && current.height === height
          ? current
          : { width, height },
      );
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(container);
    return () => observer.disconnect();
  }, []);
  const stopAnimations = useCallback(() => {
    animationsRef.current.forEach((animation) => animation.cancel());
    animationsRef.current = [];
    if (timerRef.current) window.clearTimeout(timerRef.current);
    if (swapTimerRef.current) window.clearTimeout(swapTimerRef.current);
    timerRef.current = 0;
    swapTimerRef.current = 0;
  }, []);
  useEffect(() => stopAnimations, [stopAnimations]);
  useEffect(() => {
    if (editing || transition || desiredActive === shownActive) return;
    setTransition({ to: desiredActive, grid: gridRef.current });
  }, [desiredActive, editing, shownActive, transition]);
  useEffect(() => {
    if (!transition || editing) return;
    const settings = configRef.current;
    const finish = () => {
      stopAnimations();
      setShownActive(transition.to);
      setTransition(null);
      settings.onComplete?.(transition.to);
    };
    if (
      !transition.grid.pixels.length ||
      window.matchMedia("(prefers-reduced-motion: reduce)").matches
    ) {
      finish();
      return;
    }
    const total = Math.max(200, settings.duration);
    const pixelMs = clamp(settings.pixelDuration, 60, total);
    const spread = Math.max(0, total - pixelMs);
    const endScale = coverScale(
      transition.grid.size,
      transition.grid.gap,
      settings.pixelRadius,
    );
    const startScale = clamp(settings.pixelScale, 0.05, 1) * endScale;
    const frames: Keyframe[] = [
      {
        offset: 0,
        opacity: 0,
        transform: `rotate(${settings.pixelSpin}deg) scale(${startScale})`,
      },
      ...(settings.fade
        ? []
        : [
            {
              offset: 0.001,
              opacity: 1,
              transform: `rotate(${settings.pixelSpin}deg) scale(${startScale})`,
            },
          ]),
      {
        offset: 0.42,
        opacity: 1,
        transform: `rotate(0deg) scale(${endScale})`,
      },
      {
        offset: 0.58,
        opacity: 1,
        transform: `rotate(0deg) scale(${endScale})`,
      },
      {
        offset: 1,
        opacity: 0,
        transform: `rotate(${-settings.pixelSpin * 0.18}deg) scale(${endScale * 1.04})`,
      },
    ];
    transition.grid.pixels.forEach((pixel, index) => {
      const pixelElement = pixelRefs.current[index];
      if (!pixelElement) return;
      const timing: KeyframeAnimationOptions = {
        duration: pixelMs,
        delay: pixel.offset * spread,
        easing: settings.easing,
        fill: "both",
      };
      animationsRef.current.push(pixelElement.animate(frames, timing));
    });
    swapTimerRef.current = window.setTimeout(
      () => setShownActive(transition.to),
      Math.max(80, total * 0.5),
    );
    timerRef.current = window.setTimeout(finish, total + 34);
    return stopAnimations;
  }, [editing, stopAnimations, transition]);
  const requestActive = useCallback(
    (next: boolean) => {
      if (active === undefined) setInternalActive(next);
      onActiveChange?.(next);
    },
    [active, onActiveChange],
  );
  const interactionProps = editing
    ? { tabIndex: -1 }
    : trigger === "hover"
      ? {
          onMouseEnter: () => requestActive(true),
          onMouseLeave: () => requestActive(false),
          onFocus: () => requestActive(true),
          onBlur: () => requestActive(false),
          tabIndex: 0,
        }
      : trigger === "click"
        ? {
            onClick: () => requestActive(!desiredActive),
            onKeyDown: (event: KeyboardEvent<HTMLDivElement>) => {
              if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                requestActive(!desiredActive);
              }
            },
            role: "button",
            tabIndex: 0,
          }
        : {};
  const renderLayer = (content: ReactNode, index: number) => {
    const isShown = index === (shownActive ? 1 : 0);
    return (
      <div
        key={index}
        className="rb-pixel-swap__layer"
        data-visible={editing || isShown}
        style={{ zIndex: isShown ? 2 : 1 }}
        aria-hidden={!isShown}
      >
        {content}
      </div>
    );
  };
  return (
    <div
      ref={containerRef}
      className={cn("rb-pixel-swap", className, editing && "is-editing")}
      style={{ aspectRatio, ...style }}
      data-active={shownActive}
      data-transitioning={Boolean(transition)}
      aria-label="Compare traditional CMS and Sandbox CMS"
      {...interactionProps}
    >
      {renderLayer(firstContent, 0)}
      {renderLayer(secondContent, 1)}
      {transition && !editing && (
        <div className="rb-pixel-swap__grid" aria-hidden="true">
          {transition.grid.pixels.map((pixel, index) => (
            <div
              key={pixel.id}
              ref={(element) => {
                pixelRefs.current[index] = element;
              }}
              className="rb-pixel-swap__pixel"
              style={{
                left: pixel.left,
                top: pixel.top,
                width: transition.grid.size,
                height: transition.grid.size,
                borderRadius: `${clamp(pixelRadius, 0, 50)}%`,
                background: transition.to ? "var(--accent)" : "#171714",
              }}
            />
          ))}
        </div>
      )}
      <span className="rb-pixel-swap__hint">
        {hintContent ?? `${trigger === "hover" ? "Hover" : "Click"} to transform ↗`}
      </span>
    </div>
  );
}

export function ScrollExpand({
  image,
  alt,
  title,
  hint,
  mediaContent,
  titleContent,
  hintContent,
  editing = false,
  startWidth,
  startHeight,
  startRadius,
  endRadius,
  mediaZoom,
  scrollDistance,
  smoothing,
  overlay,
}: {
  image?: string;
  alt?: string;
  title?: string;
  hint?: string;
  mediaContent?: ReactNode;
  titleContent?: ReactNode;
  hintContent?: ReactNode;
  editing?: boolean;
  startWidth: number;
  startHeight: number;
  startRadius: number;
  endRadius: number;
  mediaZoom: number;
  scrollDistance: number;
  smoothing: number;
  overlay: number;
}) {
  const root = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!motionSafe()) return;
    const element = root.current;
    if (!element) return;
    let target = 0, current = 0, frame = 0, visible = false;
    const animate = () => {
      current += (target - current) * Math.max(0.04, smoothing);
      element.style.setProperty("--expand", current.toFixed(3));
      frame = Math.abs(target - current) > 0.001
        ? requestAnimationFrame(animate)
        : 0;
    };
    const measure = () => {
      if (!visible) return;
      const rect = element.getBoundingClientRect();
      const viewport = window.innerHeight;
      target = Math.max(0, Math.min(1, (viewport - rect.top) / (viewport + scrollDistance * 3)));
      if (!frame) frame = requestAnimationFrame(animate);
    };
    const observer = new IntersectionObserver(([entry]) => {
      visible = entry.isIntersecting;
      if (visible) measure();
      else if (frame) {
        cancelAnimationFrame(frame);
        frame = 0;
      }
    }, { rootMargin: "120px" });
    observer.observe(element);
    window.addEventListener("scroll", measure, { passive: true });
    window.addEventListener("resize", measure, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", measure);
      window.removeEventListener("resize", measure);
      cancelAnimationFrame(frame);
    };
  }, [scrollDistance, smoothing]);
  return (
    <div
      className={cn("scroll-expand", editing && "is-editing")}
      ref={root}
      style={
        {
          "--start-w": `${startWidth}%`,
          "--start-h": `${startHeight}vh`,
          "--start-radius": `${startRadius}px`,
          "--end-radius": `${endRadius}px`,
          "--media-zoom": mediaZoom,
          "--overlay": overlay / 100,
        } as CSSProperties
      }
    >
      <div className="scroll-media">
        {mediaContent ?? <img src={image} alt={alt} loading="lazy" />}
        <div className="scroll-overlay" />
        {titleContent ?? <h2>{title}</h2>}
        <div className="scroll-hint">{hintContent ?? hint}</div>
      </div>
    </div>
  );
}

export function AccordionGallery({
  items,
  grayscale,
  parallax,
  tilt,
  gap,
  radius,
  expandRatio,
  orientation,
  duration,
  trigger,
  selectedId,
  onSelect,
  onReorder,
  renderImage,
  renderCaption,
  renderNumber,
  editing = false,
}: {
  items: Item[];
  grayscale: boolean;
  parallax: number;
  tilt: number;
  gap: number;
  radius: number;
  expandRatio: number;
  orientation: string;
  duration: number;
  trigger: string;
  selectedId?: string;
  onSelect: (id: string) => void;
  onReorder: (active: string, over: string) => void;
  renderImage?: (item: Item, index: number) => ReactNode;
  renderCaption?: (item: Item, index: number) => ReactNode;
  renderNumber?: (item: Item, index: number) => ReactNode;
  editing?: boolean;
}) {
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRefs = useRef<(HTMLElement | null)[]>([]);
  const mediaRefs = useRef<(HTMLElement | null)[]>([]);
  const barRefs = useRef<(HTMLElement | null)[]>([]);
  const textRefs = useRef<(HTMLElement | null)[]>([]);
  const timelineRef = useRef<gsap.core.Timeline | null>(null);
  const firstRun = useRef(true);
  const mediaSize = useRef(320);
  const vertical = orientation === "vertical";
  const [active, setActive] = useState(
    Math.min(1, Math.max(items.length - 1, 0)),
  );
  const reduced = !motionSafe();
  const applyLayout = useCallback(
    (animate: boolean) => {
      if (!panelRefs.current.length) return;
      const ratio = clamp(expandRatio, 0.2, 0.9);
      const grow =
        items.length > 1 ? (ratio * (items.length - 1)) / (1 - ratio) : 1;
      timelineRef.current?.kill();
      const timeline = gsap.timeline();
      panelRefs.current.forEach((panel, index) => {
        if (!panel) return;
        const isActive = editing || index === active;
        const rotation = isActive ? 0 : index < active ? tilt : -tilt;
        timeline.to(
          panel,
          {
            flexGrow: editing ? 1 : isActive ? grow : 1,
            ...(vertical
              ? { rotateX: editing ? 0 : -rotation }
              : { rotateY: editing ? 0 : rotation }),
            duration: animate && !reduced ? duration : 0,
            ease: "power3.out",
          },
          0,
        );
        const media = mediaRefs.current[index];
        if (media)
          timeline.to(
            media,
            {
              xPercent: -50,
              yPercent: -50,
              x: vertical
                ? 0
                : isActive
                  ? 0
                  : clamp(active - index, -1.5, 1.5) *
                    parallax *
                    mediaSize.current *
                    0.06,
              y: vertical
                ? isActive
                  ? 0
                  : clamp(active - index, -1.5, 1.5) *
                    parallax *
                    mediaSize.current *
                    0.06
                : 0,
              "--ag-gray": editing ? 0 : grayscale ? (isActive ? 0 : 1) : 0,
              "--ag-dim": editing || isActive ? 0 : 0.35,
              duration: animate && !reduced ? duration : 0,
              ease: "power3.out",
            },
            0,
          );
        const caption = [
          barRefs.current[index],
          textRefs.current[index],
        ].filter(Boolean);
        timeline.to(
          caption,
          {
            opacity: editing || isActive ? 1 : 0,
            x: editing || isActive ? 0 : -14,
            duration: animate && !reduced ? duration : 0,
            stagger: reduced ? 0 : 0.06,
            ease: "power3.out",
          },
          0,
        );
      });
      timelineRef.current = timeline;
    },
    [
      active,
      duration,
      editing,
      expandRatio,
      grayscale,
      items.length,
      parallax,
      reduced,
      tilt,
      vertical,
    ],
  );
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => {
      const rect = root.getBoundingClientRect();
      const total = vertical ? rect.height : rect.width;
      const usable = Math.max(total - gap * (items.length - 1), 120);
      mediaSize.current = Math.max(
        140,
        usable * clamp(expandRatio, 0.2, 0.9) * 1.22,
      );
      root.style.setProperty("--ag-media-size", `${mediaSize.current}px`);
      applyLayout(!firstRun.current);
    };
    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(root);
    return () => observer.disconnect();
  }, [applyLayout, expandRatio, gap, items.length, vertical]);
  useEffect(() => {
    applyLayout(!firstRun.current);
    firstRun.current = false;
  }, [applyLayout]);
  useEffect(
    () => () => {
      timelineRef.current?.kill();
    },
    [],
  );
  const keyDown = (index: number, event: KeyboardEvent<HTMLElement>) => {
    if (["ArrowRight", "ArrowDown"].includes(event.key)) {
      event.preventDefault();
      setActive((index + 1) % items.length);
      panelRefs.current[(index + 1) % items.length]?.focus();
    } else if (["ArrowLeft", "ArrowUp"].includes(event.key)) {
      event.preventDefault();
      const next = (index - 1 + items.length) % items.length;
      setActive(next);
      panelRefs.current[next]?.focus();
    }
  };
  return (
    <div
      ref={rootRef}
      className={cn("rb-accordion-gallery", vertical && "is-vertical", editing && "is-editing")}
      style={
        {
          "--ag-gap": `${gap}px`,
          "--ag-radius": `${radius}px`,
          height: vertical ? "760px" : "560px",
        } as CSSProperties
      }
      role="list"
      aria-label="Selected work image gallery"
    >
      {items.map((item, index) => {
        const isActive = index === active;
        return (
          <article
            key={item.id}
            ref={(element) => {
              panelRefs.current[index] = element;
            }}
            className={cn(
              "rb-ag-panel",
              isActive && "is-active",
              selectedId === item.id && "cms-selected",
            )}
            draggable
            onDragStart={(event) =>
              event.dataTransfer.setData("text/plain", item.id)
            }
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) =>
              onReorder(event.dataTransfer.getData("text/plain"), item.id)
            }
            onMouseEnter={() => !editing && trigger === "hover" && setActive(index)}
            onFocus={() => setActive(index)}
            onClick={() => {
              setActive(index);
              onSelect(item.id);
            }}
            onKeyDown={(event) => keyDown(index, event)}
            role="listitem"
            tabIndex={0}
            aria-current={isActive ? "true" : undefined}
            aria-label={`${item.title}: ${item.text ?? ""}`}
            style={{
              background: item.background,
              borderRadius: item.cardRadius ?? radius,
              boxShadow: item.cardShadow ? `0 ${item.cardShadow / 2}px ${item.cardShadow}px rgb(0 0 0 / .28)` : undefined,
              padding: item.spacing,
              textAlign: item.cardAlign,
            }}
          >
            <span className="rb-ag-panel__frame">
              <span
                ref={(element) => {
                  mediaRefs.current[index] = element;
                }}
                className="rb-ag-panel__media"
              >
                {renderImage?.(item, index) ?? <img
                    src={item.image}
                    alt={item.alt ?? item.title}
                    draggable="false"
                    loading={index < 2 ? "eager" : "lazy"}
                  />}
              </span>
              <span className="rb-ag-panel__overlay" aria-hidden="true" />
              <span className="rb-ag-panel__number">
                {renderNumber?.(item, index) ?? `0${index + 1}`}
              </span>
            </span>
            <span className="rb-ag-panel__label">
              <span
                ref={(element) => {
                  barRefs.current[index] = element;
                }}
                className="rb-ag-panel__bar"
              />
              <span
                ref={(element) => {
                  textRefs.current[index] = element;
                }}
                className="rb-ag-panel__text"
              >
                {renderCaption?.(item, index) ?? <><small>{item.text}</small><strong>{item.title}</strong></>}
              </span>
            </span>
          </article>
        );
      })}
    </div>
  );
}
